import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  TextField,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  InputAdornment,
  Skeleton,
  Stack,
  alpha,
  useTheme,
  Button,
  IconButton,
  Tooltip,
  Grow,
  Badge,
  Divider,
  useMediaQuery,
} from "@mui/material";
import {
  Cake,
  Search,
  PersonOutline,
  Phone,
  CakeOutlined,
  CalendarMonthOutlined,
  WhatsApp,
  ArrowForward,
  AutoAwesome,
  EmojiEvents,
  Today,
  DateRange,
  AllInclusive,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import moment from "moment";

import useAxiosPrivate from "app/hooks/useAxiosPrivate";
import { styled, keyframes } from "@mui/system";

// ─── KEYFRAME ANIMATIONS ────────────────────────────────────────────────────

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  25% { transform: translateY(-8px) rotate(2deg); }
  50% { transform: translateY(-4px) rotate(-1deg); }
  75% { transform: translateY(-10px) rotate(1deg); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.15); opacity: 1; }
`;

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  50% { opacity: 1; transform: scale(1) rotate(180deg); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to { opacity: 1; transform: translateY(0); }
`;

// ─── COLOR PALETTE ──────────────────────────────────────────────────────────

const BIRTHDAY_PALETTE = {
  primary: "#F59E0B",       // Warm Amber
  primaryLight: "#FCD34D",  // Light Gold
  primaryDark: "#D97706",   // Deep Amber
  accent: "#F97316",        // Orange accent
  glow: "#FFFBEB",          // Warm glow
  gradientStart: "#FBBF24",
  gradientEnd: "#F97316",
  surface: "rgba(251, 191, 36, 0.04)",
  surfaceHover: "rgba(251, 191, 36, 0.08)",
};

// ─── SPARKLE PARTICLE COMPONENT ─────────────────────────────────────────────

const SparkleParticle = styled("div")(({ delay, left, top, size }) => ({
  position: "absolute",
  left: `${left}%`,
  top: `${top}%`,
  width: size || 6,
  height: size || 6,
  borderRadius: "50%",
  background: BIRTHDAY_PALETTE.primaryLight,
  animation: `${sparkle} ${2 + Math.random() * 2}s ease-in-out ${delay}s infinite`,
  pointerEvents: "none",
  zIndex: 1,
}));

// ─── STYLED COMPONENTS ───────────────────────────────────────────────────────

const HeroHeader = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  background: `linear-gradient(135deg, ${BIRTHDAY_PALETTE.glow} 0%, ${alpha(BIRTHDAY_PALETTE.primary, 0.06)} 50%, ${alpha(BIRTHDAY_PALETTE.accent, 0.04)} 100%)`,
  padding: theme.spacing(4),
  borderRadius: 24,
  marginBottom: theme.spacing(3),
  border: `1px solid ${alpha(BIRTHDAY_PALETTE.primary, 0.15)}`,
  backdropFilter: "blur(20px)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: -60,
    right: -60,
    width: 200,
    height: 200,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${alpha(BIRTHDAY_PALETTE.primaryLight, 0.15)} 0%, transparent 70%)`,
    animation: `${float} 6s ease-in-out infinite`,
  },
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -40,
    left: -40,
    width: 150,
    height: 150,
    borderRadius: "50%",
    background: `radial-gradient(circle, ${alpha(BIRTHDAY_PALETTE.accent, 0.1)} 0%, transparent 70%)`,
    animation: `${float} 8s ease-in-out infinite 1s`,
  },
}));

const StatCard = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1.5, 2),
  borderRadius: 16,
  background: alpha(theme.palette.background.paper, 0.7),
  backdropFilter: "blur(10px)",
  border: `1px solid ${alpha(BIRTHDAY_PALETTE.primary, 0.12)}`,
  minWidth: 90,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 8px 24px ${alpha(BIRTHDAY_PALETTE.primary, 0.15)}`,
    borderColor: alpha(BIRTHDAY_PALETTE.primary, 0.3),
  },
}));

const FilterPill = styled(Button)(({ theme, active }) => ({
  borderRadius: 50,
  padding: theme.spacing(0.75, 2.5),
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.85rem",
  minHeight: 38,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  border: `1.5px solid ${active ? BIRTHDAY_PALETTE.primary : alpha(theme.palette.divider, 0.3)}`,
  background: active
    ? `linear-gradient(135deg, ${BIRTHDAY_PALETTE.primary}, ${BIRTHDAY_PALETTE.accent})`
    : alpha(theme.palette.background.paper, 0.6),
  color: active ? "#fff" : theme.palette.text.secondary,
  backdropFilter: "blur(10px)",
  boxShadow: active
    ? `0 4px 16px ${alpha(BIRTHDAY_PALETTE.primary, 0.35)}`
    : "none",
  "&:hover": {
    background: active
      ? `linear-gradient(135deg, ${BIRTHDAY_PALETTE.primaryDark}, ${BIRTHDAY_PALETTE.accent})`
      : alpha(BIRTHDAY_PALETTE.primary, 0.06),
    borderColor: BIRTHDAY_PALETTE.primary,
    transform: "translateY(-1px)",
  },
}));

const GlassCard = styled(Card)(({ theme, istoday }) => ({
  borderRadius: 20,
  background: istoday === "true"
    ? `linear-gradient(135deg, ${alpha(BIRTHDAY_PALETTE.glow, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`
    : alpha(theme.palette.background.paper, 0.85),
  backdropFilter: "blur(20px)",
  border: `1px solid ${istoday === "true"
    ? alpha(BIRTHDAY_PALETTE.primary, 0.25)
    : alpha(theme.palette.divider, 0.3)}`,
  boxShadow: istoday === "true"
    ? `0 8px 32px ${alpha(BIRTHDAY_PALETTE.primary, 0.12)}, inset 0 1px 0 ${alpha(BIRTHDAY_PALETTE.primaryLight, 0.2)}`
    : `0 2px 12px ${alpha(theme.palette.common.black, 0.04)}`,
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  height: "100%",
  "&:hover": {
    transform: "translateY(-6px) scale(1.01)",
    boxShadow: `0 20px 40px ${alpha(BIRTHDAY_PALETTE.primary, 0.18)}, inset 0 1px 0 ${alpha(BIRTHDAY_PALETTE.primaryLight, 0.3)}`,
    borderColor: alpha(BIRTHDAY_PALETTE.primary, 0.4),
    "& .card-glow": {
      opacity: 1,
    },
    "& .card-arrow": {
      transform: "translateX(4px)",
      opacity: 1,
    },
  },
}));

const CardGlow = styled("div")({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: 3,
  background: `linear-gradient(90deg, ${BIRTHDAY_PALETTE.primary}, ${BIRTHDAY_PALETTE.primaryLight}, ${BIRTHDAY_PALETTE.accent})`,
  backgroundSize: "200% auto",
  animation: `${shimmer} 3s linear infinite`,
  opacity: 0,
  transition: "opacity 0.4s ease",
});

const TodayBadge = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 12,
  right: 12,
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: theme.spacing(0.4, 1.2),
  borderRadius: 20,
  background: `linear-gradient(135deg, ${BIRTHDAY_PALETTE.primary}, ${BIRTHDAY_PALETTE.accent})`,
  color: "#fff",
  fontSize: "0.65rem",
  fontWeight: 700,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  boxShadow: `0 2px 8px ${alpha(BIRTHDAY_PALETTE.primary, 0.4)}`,
  "& svg": {
    fontSize: 12,
    animation: `${pulse} 2s ease-in-out infinite`,
  },
}));

const StyledAvatar = styled(Avatar)(({ theme, istoday, hasimage }) => ({
  width: 60,
  height: 60,
  border: istoday === "true"
    ? `3px solid ${BIRTHDAY_PALETTE.primary}`
    : `2px solid ${alpha(theme.palette.divider, 0.3)}`,
  boxShadow: istoday === "true"
    ? `0 0 0 3px ${alpha(BIRTHDAY_PALETTE.primary, 0.15)}, 0 4px 12px ${alpha(BIRTHDAY_PALETTE.primary, 0.2)}`
    : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
  transition: "all 0.3s ease",
  ...(!hasimage && {
    background: `linear-gradient(135deg, ${alpha(BIRTHDAY_PALETTE.primary, 0.12)}, ${alpha(BIRTHDAY_PALETTE.accent, 0.08)})`,
    color: BIRTHDAY_PALETTE.primary,
  }),
}));

const ActionButton = styled(IconButton)(({ theme, variant }) => ({
  width: 34,
  height: 34,
  borderRadius: 10,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  ...(variant === "whatsapp" && {
    background: alpha("#25D366", 0.1),
    color: "#25D366",
    "&:hover": {
      background: "#25D366",
      color: "#fff",
      transform: "scale(1.1)",
      boxShadow: `0 4px 12px ${alpha("#25D366", 0.4)}`,
    },
  }),
  ...(variant === "phone" && {
    background: alpha(BIRTHDAY_PALETTE.primary, 0.1),
    color: BIRTHDAY_PALETTE.primary,
    "&:hover": {
      background: BIRTHDAY_PALETTE.primary,
      color: "#fff",
      transform: "scale(1.1)",
      boxShadow: `0 4px 12px ${alpha(BIRTHDAY_PALETTE.primary, 0.4)}`,
    },
  }),
}));

const SearchField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 14,
    background: alpha(theme.palette.background.paper, 0.7),
    backdropFilter: "blur(10px)",
    transition: "all 0.3s ease",
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    "&:hover": {
      borderColor: alpha(BIRTHDAY_PALETTE.primary, 0.3),
      boxShadow: `0 0 0 3px ${alpha(BIRTHDAY_PALETTE.primary, 0.06)}`,
    },
    "&.Mui-focused": {
      borderColor: BIRTHDAY_PALETTE.primary,
      boxShadow: `0 0 0 3px ${alpha(BIRTHDAY_PALETTE.primary, 0.12)}`,
    },
    "& fieldset": { border: "none" },
  },
}));

// ─── SPARKLES BACKGROUND COMPONENT ──────────────────────────────────────────

const SparklesBackground = React.memo(() => {
  const particles = useMemo(() =>
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      delay: Math.random() * 3,
      size: 4 + Math.random() * 6,
    })), []
  );

  return (
    <>
      {particles.map((p) => (
        <SparkleParticle
          key={p.id}
          left={p.left}
          top={p.top}
          delay={p.delay}
          size={p.size}
        />
      ))}
    </>
  );
});

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────

export default function Birthday() {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [day, setDay] = useState("Today");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [allCustomers, setAllCustomers] = useState([]);

  // Fetch data ONLY once on mount
  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await axiosPrivate.post("LoadReferenceBirthday.php");
        const result = response.data;
        if (isMounted) {
          if (Array.isArray(result.Data)) {
            setAllCustomers(result.Data);
          } else {
            setAllCustomers([]);
            console.error("Unexpected data format:", result);
          }
        }
      } catch (error) {
        console.error("Error fetching greetings data:", error);
        if (isMounted) setAllCustomers([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [axiosPrivate]);

  // Compute stats
  const stats = useMemo(() => {
    const today = allCustomers.filter((c) => c?.DateCategory === "Today").length;
    const tomorrow = allCustomers.filter((c) => c?.DateCategory === "Tomorrow").length;
    const total = allCustomers.length;
    return { today, tomorrow, total };
  }, [allCustomers]);

  // Client-side filtering
  const filteredCustomers = useMemo(() => {
    return allCustomers.filter((c) => {
      if (day !== "All" && c?.DateCategory !== day) return false;
      if (search) {
        const searchLower = search.toLowerCase();
        const nameMatch = (c?.FamilyMember || "").toLowerCase().includes(searchLower);
        const mobileMatch = (c?.Mobile || "").toLowerCase().includes(searchLower);
        if (!nameMatch && !mobileMatch) return false;
      }
      return true;
    });
  }, [allCustomers, day, search]);

  const handleWhatsApp = useCallback((mobile, name) => {
    if (!mobile) return;
    const message = encodeURIComponent(`Happy Birthday ${name}! 🎂🎉 Wishing you a wonderful year ahead filled with joy and happiness!`);
    window.open(`https://wa.me/91${mobile}?text=${message}`, "_blank");
  }, []);

  const handleCall = useCallback((mobile) => {
    if (!mobile) return;
    window.open(`tel:${mobile}`);
  }, []);

  const filters = [
    { label: "Today", value: "Today", icon: <Today sx={{ fontSize: 16 }} />, count: stats.today },
    { label: "Tomorrow", value: "Tomorrow", icon: <DateRange sx={{ fontSize: 16 }} />, count: stats.tomorrow },
    { label: "All", value: "All", icon: <AllInclusive sx={{ fontSize: 16 }} />, count: stats.total },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1600, margin: "0 auto" }}>
      {/* ── HERO HEADER ── */}
      <HeroHeader>
        <SparklesBackground />

        <Grid container spacing={3} alignItems="center" sx={{ position: "relative", zIndex: 2 }}>
          {/* Title & Subtitle */}
          <Grid item xs={12} md={5}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: "14px",
                display: "flex", alignItems: "center", justifyContent: "center",
                background: `linear-gradient(135deg, ${BIRTHDAY_PALETTE.primary}, ${BIRTHDAY_PALETTE.accent})`,
                boxShadow: `0 4px 16px ${alpha(BIRTHDAY_PALETTE.primary, 0.35)}`,
              }}>
                <Cake sx={{ color: "#fff", fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, ${BIRTHDAY_PALETTE.primaryDark}, ${BIRTHDAY_PALETTE.accent})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: "-0.02em",
                }}>
                  Birthday Wishes
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.25, fontSize: "0.8rem" }}>
                  Never miss a chance to brighten someone's day 🎉
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Stats */}
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1.5} justifyContent={{ xs: "flex-start", md: "center" }}>
              {filters.map((f) => (
                <StatCard
                  key={f.value}
                  onClick={() => setDay(f.value)}
                  sx={{
                    ...(day === f.value && {
                      borderColor: alpha(BIRTHDAY_PALETTE.primary, 0.4),
                      background: alpha(BIRTHDAY_PALETTE.primary, 0.06),
                    }),
                  }}
                >
                  <Typography variant="h5" sx={{
                    fontWeight: 800,
                    color: day === f.value ? BIRTHDAY_PALETTE.primary : "text.primary",
                    lineHeight: 1,
                  }}>
                    {f.count}
                  </Typography>
                  <Typography variant="caption" sx={{
                    color: "text.secondary",
                    fontWeight: 600,
                    fontSize: "0.65rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mt: 0.5,
                  }}>
                    {f.label}
                  </Typography>
                </StatCard>
              ))}
            </Stack>
          </Grid>

          {/* Search */}
          <Grid item xs={12} md={4}>
            <SearchField
              placeholder="Search name or mobile..."
              variant="outlined"
              size="small"
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: BIRTHDAY_PALETTE.primary, fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {/* Filter Pills */}
        <Stack direction="row" spacing={1} sx={{ mt: 2.5, position: "relative", zIndex: 2 }}>
          {filters.map((f) => (
            <FilterPill
              key={f.value}
              active={day === f.value ? 1 : 0}
              onClick={() => setDay(f.value)}
              startIcon={f.icon}
            >
              {f.label}
              {f.count > 0 && (
                <Box component="span" sx={{
                  ml: 0.8,
                  px: 0.8,
                  py: 0.1,
                  borderRadius: 6,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  background: day === f.value ? alpha("#fff", 0.25) : alpha(BIRTHDAY_PALETTE.primary, 0.1),
                  color: day === f.value ? "#fff" : BIRTHDAY_PALETTE.primary,
                }}>
                  {f.count}
                </Box>
              )}
            </FilterPill>
          ))}
        </Stack>
      </HeroHeader>

      {/* ── CONTENT GRID ── */}
      {loading ? (
        <Grid container spacing={2.5}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((idx) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={idx}>
              <Skeleton
                variant="rounded"
                height={200}
                sx={{
                  borderRadius: 5,
                  animation: `${shimmer} 1.5s ease-in-out infinite`,
                  background: `linear-gradient(90deg, ${alpha(BIRTHDAY_PALETTE.primary, 0.04)} 25%, ${alpha(BIRTHDAY_PALETTE.primary, 0.08)} 50%, ${alpha(BIRTHDAY_PALETTE.primary, 0.04)} 75%)`,
                  backgroundSize: "200% 100%",
                }}
              />
            </Grid>
          ))}
        </Grid>
      ) : filteredCustomers.length === 0 ? (
        <Box sx={{
          textAlign: "center",
          py: 12,
          px: 3,
          borderRadius: 6,
          background: alpha(BIRTHDAY_PALETTE.primary, 0.02),
          border: `2px dashed ${alpha(BIRTHDAY_PALETTE.primary, 0.12)}`,
        }}>
          <Box sx={{
            width: 80, height: 80, borderRadius: "50%", mx: "auto", mb: 3,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: alpha(BIRTHDAY_PALETTE.primary, 0.06),
          }}>
            <CakeOutlined sx={{ fontSize: 40, color: alpha(BIRTHDAY_PALETTE.primary, 0.4) }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "text.secondary", mb: 1 }}>
            No birthdays found
          </Typography>
          <Typography variant="body2" sx={{ color: "text.disabled", maxWidth: 320, mx: "auto" }}>
            Try switching filters or adjusting your search to find upcoming celebrations.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2.5}>
          {filteredCustomers.map((c, index) => {
            const isToday = c?.DateCategory === "Today";
            const pictureSrc = c.Picture
              ? c.Picture.startsWith("http")
                ? c.Picture
                : `https://backend.dahiyainsurance.com/Upload/profile/${c.Picture}`
              : null;

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={`${c?.ID}-${index}`}>
                <Grow in timeout={300 + index * 80} style={{ transformOrigin: "0 100%" }}>
                  <div style={{ height: "100%" }}>
                    <GlassCard istoday={isToday.toString()}>
                      <CardGlow className="card-glow" />

                      {/* Today Badge */}
                      {isToday && (
                        <TodayBadge>
                          <AutoAwesome /> TODAY
                        </TodayBadge>
                      )}

                      <CardContent sx={{ p: 2.5, pb: "16px !important" }}>
                        {/* Profile Row */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                          <StyledAvatar
                            src={pictureSrc || undefined}
                            alt={c.FamilyMember}
                            istoday={isToday.toString()}
                            hasimage={pictureSrc ? "true" : undefined}
                          >
                            {!pictureSrc && <PersonOutline sx={{ fontSize: 28 }} />}
                          </StyledAvatar>

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 700,
                                textTransform: "capitalize",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontSize: "0.95rem",
                                lineHeight: 1.3,
                              }}
                            >
                              {(c?.FamilyMember || "Unknown").toLowerCase()}
                            </Typography>

                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                              <Phone sx={{ fontSize: 13, color: "text.disabled" }} />
                              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem" }}>
                                {c?.Mobile || "No Mobile"}
                              </Typography>
                            </Stack>
                          </Box>
                        </Box>

                        {/* Info Chips */}
                        <Stack direction="row" spacing={0.8} sx={{ mb: 2, flexWrap: "wrap", gap: 0.8 }}>
                          <Chip
                            size="small"
                            icon={<Cake sx={{ fontSize: 13, color: isToday ? "#fff !important" : `${BIRTHDAY_PALETTE.primary} !important` }} />}
                            label={isToday ? "🎂 Birthday Today!" : `${c.YearsCompleted} Years`}
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.7rem",
                              height: 26,
                              borderRadius: "8px",
                              ...(isToday
                                ? {
                                    background: `linear-gradient(135deg, ${BIRTHDAY_PALETTE.primary}, ${BIRTHDAY_PALETTE.accent})`,
                                    color: "#fff",
                                    boxShadow: `0 2px 8px ${alpha(BIRTHDAY_PALETTE.primary, 0.35)}`,
                                  }
                                : {
                                    background: alpha(BIRTHDAY_PALETTE.primary, 0.08),
                                    color: BIRTHDAY_PALETTE.primaryDark,
                                    border: `1px solid ${alpha(BIRTHDAY_PALETTE.primary, 0.15)}`,
                                  }),
                            }}
                          />
                          <Chip
                            size="small"
                            icon={<CalendarMonthOutlined sx={{ fontSize: 13, color: "text.disabled !important" }} />}
                            label={moment(c?.Date).format("DD MMM")}
                            sx={{
                              fontSize: "0.7rem",
                              height: 26,
                              borderRadius: "8px",
                              color: "text.secondary",
                              background: alpha(theme.palette.action.hover, 0.5),
                              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                            }}
                          />
                        </Stack>

                        {/* Divider */}
                        <Divider sx={{
                          borderColor: alpha(theme.palette.divider, 0.08),
                          mb: 1.5,
                        }} />

                        {/* Actions Row */}
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack direction="row" spacing={0.8}>
                            <Tooltip title="Send WhatsApp Wish" arrow placement="top">
                              <ActionButton
                                variant="whatsapp"
                                size="small"
                                onClick={() => handleWhatsApp(c?.Mobile, (c?.FamilyMember || "").split(" ")[0])}
                              >
                                <WhatsApp sx={{ fontSize: 17 }} />
                              </ActionButton>
                            </Tooltip>
                            <Tooltip title="Call" arrow placement="top">
                              <ActionButton
                                variant="phone"
                                size="small"
                                onClick={() => handleCall(c?.Mobile)}
                              >
                                <Phone sx={{ fontSize: 17 }} />
                              </ActionButton>
                            </Tooltip>
                          </Stack>

                          <Button
                            component={Link}
                            to={
                              c?.Type === "Agent"
                                ? `/CLIAProspect/EditCLIAProspect/${c?.MainMemberID}`
                                : `/Prospects/ViewProspect/${c?.MainMemberID}`
                            }
                            size="small"
                            sx={{
                              textTransform: "none",
                              fontWeight: 600,
                              fontSize: "0.78rem",
                              color: "text.secondary",
                              borderRadius: 2,
                              px: 1.5,
                              "&:hover": {
                                color: BIRTHDAY_PALETTE.primary,
                                background: alpha(BIRTHDAY_PALETTE.primary, 0.06),
                              },
                            }}
                            endIcon={
                              <ArrowForward
                                className="card-arrow"
                                sx={{
                                  fontSize: "14px !important",
                                  transition: "all 0.3s ease",
                                  opacity: 0.5,
                                }}
                              />
                            }
                          >
                            View
                          </Button>
                        </Stack>
                      </CardContent>
                    </GlassCard>
                  </div>
                </Grow>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
}
